/* eslint-disable no-console */
// @ts-nocheck

import React, { useEffect, useState, useRef } from "react";
import "./Map.css";
import "leaflet/dist/leaflet.css";
import { MapContainer, FeatureGroup, TileLayer } from "react-leaflet";
import ReactLeafletGoogleLayer from "react-leaflet-google-layer";
import GeomanWrapper, { ColoredDivIcon, LayerInformationIcon, NumberedDivIcon } from "./Geoman";
import FreeDrawWrapper from "./FreeDrawWrapper";
import type { FeatureCollection } from "geojson";
import { splitPixelPolygonByLine } from "modules/aiMapping/aiMappingGeometry";
import {
  MeasuredAreaOpsContext,
  splitManualAreasAlongLine,
  eraseManualAreasWithPolygon,
  mergeManualAreasWithPolygon,
} from "./measuredAreaOps";
import Controls from "./Controls";
import GuidedControls from "./GuidedControls";
import * as L from "leaflet";
import "leaflet-geometryutil";
import { MAP_MODE } from "types/types";
import * as turf from "@turf/turf";
import { MapMeasurementTotals, MapAddress, MapOutput, MapTextBox } from "interfaces/interfaces";
import {
  addLayerFeature,
  addLayerLabelLine,
  addLayerLineLengths,
  calculateDraggedMarkerLabelPosition,
  checkForIntersects,
  clearLayer,
  getAddressObject,
  getCenterOfLayer,
  getLayerLabelPosition,
  getLayerTooltipContent,
  removeLayerLabelLine,
  removeLayerLabels,
  repositionLayerLabelLine,
  toggleDrawMode,
  updateDrawingLineToolTipContent,
} from "helpers/map";
import { meterConversions } from "constants/meterConversions";
import { ProductMeasurementJob } from "generated/graphql";
import { getValidSegmentCoords } from "helpers/mapGeometry";
import { removeLastVertex, restartDrawingMode, validateDrawingState } from "helpers/pmUtils";
import { updateDrawingPolygonToolTipContent } from "helpers/map";
import { getAddressLine2FromPlace } from "modules/projectMaps/projectMapAddress";
import { layerLabelExportMinimums, layerLabelStyle } from "./layerLabelStyles";

const defaultColor = "#f44336";
export type MapCoords = { lng: string; lat: string };

/** One corrupted layer (e.g. an AI feature with mosaic-scale coordinates)
 * shouldn't zoom the whole map out to a continent view, so only layers whose
 * bounds are finite and locally plausible participate in the initial fit. */
const saneLayerBounds = (layer: any): L.LatLngBounds | null => {
  if (typeof layer?.getBounds !== "function") return null;
  const bounds = layer.getBounds();
  if (!bounds || !bounds.isValid()) return null;
  const east = bounds.getEast();
  const west = bounds.getWest();
  const north = bounds.getNorth();
  const south = bounds.getSouth();
  if (![east, west, north, south].every(Number.isFinite)) return null;
  if (Math.abs(east - west) > 2 || Math.abs(north - south) > 2) return null;
  return bounds;
};

/** Fit the map to the union of sane layer bounds; false when there are none. */
const fitSaneLayerBounds = (
  map: L.Map,
  group: L.FeatureGroup | null,
  zoomSnap?: number
): boolean => {
  if (!map || !group) return false;
  let bounds: L.LatLngBounds | null = null;
  group.eachLayer((layer: any) => {
    const layerBounds = saneLayerBounds(layer);
    if (!layerBounds) return;
    bounds = bounds ? bounds.extend(layerBounds) : layerBounds;
  });
  if (!bounds || !bounds.isValid()) return false;

  const boundsZoom = Math.floor(map.getBoundsZoom(bounds));
  const zoomPaddingMap: Record<number, number> = {
    20: 50,
    19: 100,
    18: 200,
    17: 300,
  };
  const padding = zoomPaddingMap[boundsZoom] ?? 100;

  map.fitBounds(bounds, {
    padding: !Boolean(zoomSnap) ? [padding, padding] : undefined,
  });

  return true;
};

const fitToReferenceLocation = (
  map: L.Map,
  address: MapAddress | null,
  center: [number, number] | null
): void => {
  const lat = address?.lat ?? center?.[0];
  const lng = address?.lng ?? center?.[1];
  if (
    typeof lat !== "number" ||
    typeof lng !== "number" ||
    !Number.isFinite(lat) ||
    !Number.isFinite(lng)
  ) {
    return;
  }
  const latLon = new L.LatLng(lat, lng);
  map.panTo(latLon).fitBounds(latLon.toBounds(100));
};

const getUtf8ByteSize = (value: string): number => new TextEncoder().encode(value).length;
const MAP_WARNING_THRESHOLD_BYTES = Math.floor(1.5 * 1024 * 1024);
const MAP_WARNING_MESSAGE =
  "This measurement drawing is becoming too large to save as a single map. Please break this up into multiple maps. Use the screenshot feature to save your current work.";

const googleMutantPrototype = (L as any)?.GridLayer?.GoogleMutant?.prototype;
if (
  googleMutantPrototype &&
  typeof googleMutantPrototype._setupAttribution === "function" &&
  !googleMutantPrototype._bitumioSafeSetupAttribution
) {
  const originalSetupAttribution = googleMutantPrototype._setupAttribution;
  googleMutantPrototype._setupAttribution = function (...args: unknown[]) {
    if (!this?._map || !this?._map?._controlCorners) {
      return;
    }
    return originalSetupAttribution.apply(this, args);
  };
  googleMutantPrototype._bitumioSafeSetupAttribution = true;
}

export default function MapComponent({
  createIslandConfirmation,
  onSave,
  onChange,
  initialArea,
  initialFeatures,
  initialCenter,
  readonly,
  isTouch,
  hideLocation = false,
  guidedControls,
  goBack = false,
  goBackTo,
  goBackLabel,
  showTools,
  close,
  zoomSnap = 1,
  labelScale = 1,
  onFeatureSelect,
  saveLabel = "Update Proposal",
  highlightedFeatureId = null,
  initialAddress = null,
  onAddressChange,
  onGuidedAddressConfirmed,
  onMapReady,
  suppressCreateHandling = false,
  controlsVariant = "card",
  controlsContent,
  sidebarToolsContent,
  measuredAreaTools,
  measuredAreaToolsRef,
  hideMeasurementTotals,
  controlsCollapsed = false,
  onToggleControlsCollapse,
  mapTitle = null,
}: {
  createIslandConfirmation: (callback: () => void) => boolean;
  onSave?: ((data: MapOutput) => Promise<boolean | void> | boolean | void) | null;
  onChange?: ((data: MapOutput) => void) | null;
  initialArea?: FeatureCollection | null;
  initialFeatures?: FeatureCollection | null;
  initialCenter: [number, number] | null;
  readonly: boolean;
  isTouch: boolean;
  hideLocation?: boolean;
  guidedControls: boolean;
  goBack?: boolean;
  goBackTo?: string;
  goBackLabel?: string;
  showTools?: [keyof typeof ProductMeasurementJob];
  close?: () => void;
  zoomSnap?: number;
  labelScale?: number;
  onFeatureSelect?: (featureId: string) => void;
  saveLabel?: string;
  highlightedFeatureId?: string | null;
  initialAddress?: MapAddress | null;
  onAddressChange?: (address: MapAddress | null) => void;
  onMapReady?: (map: L.Map) => void;
  suppressCreateHandling?: boolean;
  controlsVariant?: "card" | "sidebar";
  controlsContent?: React.ReactNode;
  sidebarToolsContent?: React.ReactNode;
  measuredAreaTools?: React.ReactNode;
  measuredAreaToolsRef?: React.MutableRefObject<{
    splitLine: (points: [number, number][]) => number;
    erasePolygon: (points: [number, number][]) => number;
    mergePolygon: (points: [number, number][]) => number;
    remove: () => void;
  } | null>;
  hideMeasurementTotals?: boolean;
  controlsCollapsed?: boolean;
  onToggleControlsCollapse?: () => void;
  mapTitle?: string | null;
  onGuidedAddressConfirmed?: () => void;
}) {
  const featureGroup = React.useRef<L.FeatureGroup>(null);
  const initialAreaFeatureGroup = React.useRef<L.FeatureGroup>(null);
  const defaultLayerColor = useRef<string>(defaultColor);
  const mapMode = useRef<MAP_MODE | null>(null);
  const editingLayer = useRef<object>({
    layer: null,
    parent: null,
  });

  const [editingLayerType, setEditingLayerType] = useState<string | null>();
  const [map, setMap] = useState<L.Map>();
  const [currentDrawShape, setCurrentDrawShape] = React.useState<
    L.PM.SUPPORTED_SHAPES | "FreePolygon" | "FreePolyline" | null
  >(null);
  const [isEditing, setIsEditing] = React.useState<boolean>(false);
  const [isDragging, setIsDragging] = React.useState<boolean>(false);
  const [layerColor, setLayerColor] = React.useState<string>(defaultColor);
  const teardownRef = React.useRef(false);
  useEffect(() => {
    teardownRef.current = false;
    return () => {
      teardownRef.current = true;
    };
  }, []);
  const [layerName, setLayerName] = React.useState<string | null>();
  const [mapTotals, setMapTotals] = React.useState<MapMeasurementTotals>({
    area: {
      feet: 0,
      yards: 0,
    },
    perimeter: {
      feet: 0,
      yards: 0,
    },
  });
  const [showLineLengthsWhileMeasuring, setShowLineLengthsWhileMeasuring] =
    React.useState<boolean>(false);
  const [layerShowLineLength, setLayerShowLineLength] = React.useState<boolean>(false);
  const [showTooltip, setShowTooltip] = React.useState<boolean>(false);
  const [selectedAddress, setSelectedAddress] = React.useState<MapAddress | null>(
    initialAddress ?? null
  );
  const [twoFingerDragEnabled, setTwoFingerDragEnabled] = React.useState<boolean>(false);
  const [useOpenStreetMap, setUseOpenStreetMap] = React.useState<boolean>(false);
  const [freeDrawModeActive, setFreeDrawModeActive] = React.useState<boolean>(false);
  const [isGeneratingScreenshot, setIsGeneratingScreenshot] = React.useState<boolean>(false);
  const [textBoxes, setTextBoxes] = React.useState<MapTextBox[]>([]);
  const [selectedTextBoxId, setSelectedTextBoxId] = React.useState<string | null>(null);
  const [textToolActive, setTextToolActive] = React.useState<boolean>(false);
  const [currentGeoJsonBytes, setCurrentGeoJsonBytes] = React.useState<number>(0);
  const textBoxMarkers = useRef<Map<string, L.Marker>>(new Map());
  const activeDrawingLayer = useRef<L.Layer | null>(null);
  const highlightedLayerRef = useRef<L.Layer | null>(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const onFeatureSelectRef = useRef(onFeatureSelect);
  onFeatureSelectRef.current = onFeatureSelect;
  const onAddressChangeRef = useRef(onAddressChange);
  onAddressChangeRef.current = onAddressChange;
  const onMapReadyRef = useRef(onMapReady);
  onMapReadyRef.current = onMapReady;
  const suppressCreateHandlingRef = useRef(suppressCreateHandling);
  suppressCreateHandlingRef.current = suppressCreateHandling;

  useEffect(() => {
    if (map && onMapReadyRef.current) {
      onMapReadyRef.current(map);
    }
  }, [map]);

  useEffect(() => {
    setSelectedAddress(initialAddress ?? null);
  }, [initialAddress]);

  const safeIconUnhighlight = (layer: any): void => {
    const icon = layer?.getIcon?.();
    const fn = icon?.unhighlight;
    if (typeof fn !== "function") return;
    fn.call(icon, layer);
  };

  const safeIconChangeColor = (layer: any, color: string): void => {
    const icon = layer?.getIcon?.();
    const fn = icon?.changeColor;
    if (typeof fn !== "function") return;
    fn.call(icon, color, layer);
  };

  const safeIconHighlight = (layer: any): void => {
    const icon = layer?.getIcon?.();
    const fn = icon?.highlight;
    if (typeof fn !== "function") return;
    fn.call(icon, layer);
  };

  const syncDrawingHintTooltip = React.useCallback(
    (
      hintMarker: any,
      updater: (e: any, options?: { showTooltip?: boolean; showSegmentLength?: boolean }) => void,
      enabled: boolean
    ): void => {
      if (!hintMarker) {
        return;
      }

      if (hintMarker._drawingHintTooltipHandler) {
        hintMarker.off("move", hintMarker._drawingHintTooltipHandler);
        delete hintMarker._drawingHintTooltipHandler;
      }

      const shouldShowMeasurementTooltip =
        enabled && (showTooltip || showLineLengthsWhileMeasuring);

      if (!shouldShowMeasurementTooltip) {
        if (hintMarker.getTooltip()) {
          hintMarker.closeTooltip();
          hintMarker.unbindTooltip();
        }
        return;
      }

      if (!hintMarker.getTooltip()) {
        hintMarker.bindTooltip("", {
          permanent: false,
          offset: L.point(12, 18),
          direction: "bottom",
          opacity: 1,
        });
      }

      const handler = (e: any) =>
        updater(e, {
          showTooltip,
          showSegmentLength: showLineLengthsWhileMeasuring,
        });

      hintMarker._drawingHintTooltipHandler = handler;
      hintMarker.on("move", handler);
    },
    [showLineLengthsWhileMeasuring, showTooltip]
  );

  const addLayerEvents = (layer: L.Layer): void => {
    layer.on("pm:enable", () => {
      if (isTouch) {
        if (editingLayer.current.layer instanceof L.Marker) {
          toggleDrag();
          safeIconUnhighlight(editingLayer.current.layer);

          toggleDragLabel(true);
          setIsDragging(false);
          setIsEditing(false);
        }
      }

      // Don't disable the current layer's edit mode - let it stay enabled
      // editingLayer.current.layer?.pm?.disable();
      editingLayer.current.layer = layer;
      mapMode.current = "editing";
      setEditingLayerType("layer");
      setLayerName(layer.feature?.properties?.labelText ?? "");
      setLayerColor(layer.feature?.properties?.color);
      setLayerShowLineLength(layer.feature?.properties?.showLineLengths ?? false);
      const layerTooltipState = layer.feature?.properties?.showTooltip;
      setShowTooltip(layerTooltipState ?? false);

      // Immediately update the layer properties to ensure consistency
      if (layer.feature?.properties) {
        layer.feature.properties.showTooltip = layerTooltipState ?? false;
      }

      setIsEditing(true);
      clearLayer(map, layer);

      // Recreate the label immediately after clearing
      addLayerLabel(layer, isTouch);

      if (editingLayer.current.layer instanceof L.Marker) {
        safeIconHighlight(editingLayer.current.layer);
      }
    });

    layer.on("pm:disable", () => {
      // Clear any pending edit timeouts
      if (layer._editTimeout) {
        clearTimeout(layer._editTimeout);
        layer._editTimeout = null;
      }

      if (!layer._removing) {
        // Don't save tooltip state here since it's handled by useEffect
        addLayerLineLengths(map, editingLayer.current.layer as L.Layer, labelScale);
        addLayerLabel(layer, isTouch);

        if (editingLayer.current.layer instanceof L.Marker) {
          safeIconUnhighlight(editingLayer.current.layer);
        }
      }
      setIsEditing(false);
      editingLayer.current.layer = null;
      mapMode.current = null;

      setMapTotals(calculateTotalAreaAndPerimeter());
      emitMapChange();
    });

    layer.on("click", (le) => {
      if (mapMode.current === "drawing" || (map as any)._aiMappingDrawActive === true) {
        return;
      }

      L.DomEvent.stopPropagation(le);
      const selectedFeatureId = layer.feature?.properties?.featureId;
      if (selectedFeatureId && onFeatureSelectRef.current) {
        onFeatureSelectRef.current(selectedFeatureId);
      }

      // Load layer state when clicked (same as pm:enable)
      // Disable previous layer's edit mode before enabling new one
      if (editingLayer.current.layer && editingLayer.current.layer !== layer) {
        editingLayer.current.layer.pm?.disable();
      }
      editingLayer.current.layer = layer;
      mapMode.current = "editing";
      setEditingLayerType("layer");
      setLayerName(layer.feature?.properties?.labelText ?? "");
      setLayerColor(layer.feature?.properties?.color);
      setLayerShowLineLength(layer.feature?.properties?.showLineLengths ?? false);
      const layerTooltipState = layer.feature?.properties?.showTooltip;
      setShowTooltip(layerTooltipState ?? false);

      // Immediately update the layer properties to ensure consistency
      if (layer.feature?.properties) {
        layer.feature.properties.showTooltip = layerTooltipState ?? false;
      }

      setIsEditing(true);
      clearLayer(map, layer);

      if (editingLayer.current.layer instanceof L.Marker) {
        safeIconHighlight(editingLayer.current.layer);
      }

      if (isTouch) {
        // unhighlight previous layer
        if (editingLayer.current.layer instanceof L.Marker) {
          safeIconUnhighlight(editingLayer.current.layer);
        }

        // Enable vertex dragging for touch devices
        layer.pm.toggleEdit({ draggable: true, preventMarkerRemoval: true });

        // Ensure edit mode is enabled
        if (!layer.pm.enabled()) {
          console.log("Forcing edit mode enable for touch device");
          layer.pm.enable({ draggable: true, preventMarkerRemoval: true });
        }

        // Add a small delay to ensure edit mode is fully enabled and vertex markers are visible
        setTimeout(() => {
          if (layer.pm.enabled()) {
            console.log("Touch edit mode confirmed enabled, vertex markers should be visible");
          } else {
            console.log("Touch edit mode failed to enable");
          }
        }, 100);
      } else {
        if (layer._dragging) {
          return;
        }

        layer.pm.toggleEdit({
          allowSelfIntersection: false,
          draggable: true,
        });

        // Ensure edit mode is enabled
        if (!layer.pm.enabled()) {
          console.log("Forcing edit mode enable for non-touch device");
          layer.pm.enable({ allowSelfIntersection: false, draggable: true });
        }

        // Add a small delay to ensure edit mode is fully enabled and vertex markers are visible
        setTimeout(() => {
          if (layer.pm.enabled()) {
            console.log("Non-touch edit mode confirmed enabled, vertex markers should be visible");
          } else {
            console.log("Non-touch edit mode failed to enable");
          }
        }, 100);

        if (layer instanceof L.Marker) {
          le.target.dragging.enable();
        }
      }
    });

    layer.on("dblclick", (le) => {
      L.DomEvent.stopPropagation(le);
    });

    layer.on("pm:cut", (le) => {
      clearLayer(map, le.originalLayer);
      addLayerLineLengths(map, le.layer, labelScale);
      addLayerLabel(le.layer, isTouch);
      addLayerEvents(le.layer);

      setMapTotals(calculateTotalAreaAndPerimeter());
      emitMapChange();
    });

    layer.on("remove", () => {
      layer._removing = true;
      clearLayer(map, layer);

      if (teardownRef.current) {
        return;
      }

      setMapTotals(calculateTotalAreaAndPerimeter());
      emitMapChange();
    });

    if (!(layer instanceof L.Marker)) {
      layer.on("mouseover", () => {
        layer?.setStyle({
          weight: 5,
        });
      });

      layer.on("mouseout", () => {
        layer?.setStyle({
          weight: 3,
        });
      });
    } else if (layer instanceof L.Marker) {
      // only add pm:dragstart and pm:dragend event handlers for non touch devices
      if (!isTouch) {
        layer.on("pm:dragstart", (e) => {
          layer._previousLatlng = e.target._latlng;
          layer._dragging = true;
          safeIconUnhighlight(layer);
        });

        layer.on("pm:dragend", (e) => {
          layer._dragging = false;
          if (layer.feature.properties.labelLatLng) {
            layer.feature.properties.labelLatLng = calculateDraggedMarkerLabelPosition(
              layer,
              e.target._latlng
            );
          }
          safeIconHighlight(layer);
        });
      }

      layer.on("mouseover", () => {
        if (layer._dragging) {
          return;
        }

        safeIconHighlight(layer);
      });

      layer.on("mouseout", () => {
        if (editingLayer.current.layer != layer) {
          safeIconUnhighlight(layer);
        }
      });
    }

    layer.on("pm:edit", () => {
      // Clear and recreate tooltips when vertices are moved
      if (layer === editingLayer.current.layer) {
        // Use a debounced approach to prevent multiple rapid tooltip recreations
        if (layer._editTimeout) {
          clearTimeout(layer._editTimeout);
        }

        layer._editTimeout = setTimeout(() => {
          clearLayer(map, layer);
          addLayerLabel(layer, isTouch);
          layer._editTimeout = null;
        }, 100); // 100ms debounce
      }
      setMapTotals(calculateTotalAreaAndPerimeter());
      emitMapChange();
    });
  };

  const measuredAreaLayer = (): L.Polygon | null => {
    const layer = highlightedLayerRef.current as L.Polygon | null;
    return layer && layer instanceof L.Polygon && !(layer instanceof L.Marker) ? layer : null;
  };

  const handleChange = (e): void => {
    if (e.type === "pm:create") {
      if (suppressCreateHandlingRef.current || (map as any)._aiMappingDrawActive === true) {
        return;
      }
      const layer = e.layer;

      if (
        layer instanceof L.Polygon &&
        checkForIntersects(map, featureGroup.current, layer, createIslandConfirmation)
      ) {
        return;
      }

      addLayerFeature(layer, defaultLayerColor.current);

      if (layer instanceof L.Marker) {
        layer.setIcon(
          new ColoredDivIcon({
            color: defaultLayerColor.current,
          })
        );
      }

      if (layer instanceof L.Polyline) {
        layer?.setStyle({
          color: defaultLayerColor.current,
          weight: 3,
          fillColor: defaultLayerColor.current,
          fillOpacity: 0.3,
        });
      }

      addLayerEvents(layer);

      if (layer instanceof L.Polyline) {
        layer.feature.properties.showLineLengths = layerShowLineLength;

        addLayerLineLengths(map, layer, labelScale);
        addLayerLabel(layer, isTouch);
      }

      setMapTotals(calculateTotalAreaAndPerimeter());
      emitMapChange();
    }
  };

  const handleFreePolygonCreated = (polygon: L.Polygon): void => {
    // Remove the polygon from map first (it was added temporarily)
    polygon.removeFrom(map);

    // Add layer feature with current color first
    addLayerFeature(polygon, layerColor);

    // Add to the feature group
    featureGroup.current.addLayer(polygon);

    // Check for intersections and handle cutting if needed
    // Note: checkForIntersects may modify the polygon through cutting
    checkForIntersects(map, featureGroup.current, polygon, createIslandConfirmation);

    // Add all the standard layer functionality
    addLayerEvents(polygon);
    polygon.feature.properties.showLineLengths = layerShowLineLength;
    addLayerLineLengths(map, polygon, labelScale);
    addLayerLabel(polygon, isTouch);

    // Update measurements
    setMapTotals(calculateTotalAreaAndPerimeter());
    emitMapChange();

    // Keep the FreePolygon mode active instead of resetting
    // setCurrentDrawShape(null);
    // mapMode.current = null;
  };

  const handleFreePolylineCreated = (polyline: L.Polyline): void => {
    // Remove the polyline from map first (it was added temporarily)
    polyline.removeFrom(map);

    // Add layer feature with current color first
    addLayerFeature(polyline, layerColor);

    // Add to the feature group
    featureGroup.current.addLayer(polyline);

    // Add all the standard layer functionality
    addLayerEvents(polyline);
    polyline.feature.properties.showLineLengths = layerShowLineLength;
    addLayerLineLengths(map, polyline, labelScale);
    addLayerLabel(polyline, isTouch);

    // Update measurements
    setMapTotals(calculateTotalAreaAndPerimeter());
    emitMapChange();

    // Keep the FreePolyline mode active instead of resetting
    // setCurrentDrawShape(null);
    // mapMode.current = null;
  };

  const generateGeojson = (): FeatureCollection => {
    const newGeo: FeatureCollection = {
      type: "FeatureCollection",
      features: [],
    };
    const allLayers = featureGroup.current?.getLayers()?.length
      ? featureGroup.current.getLayers()
      : initialAreaFeatureGroup.current?.getLayers() ?? [];
    if (!allLayers) {
      return newGeo;
    }

    allLayers.forEach((layer) => {
      if (layer._removing) {
        return true;
      }

      if (layer.options.pmIgnore) {
        return true;
      }

      if (layer.options.icon instanceof LayerInformationIcon) {
        return true;
      }

      if (layer?.label) {
        layer.feature.properties.labelLatLng = layer?.label?._latlng;
      }

      if (
        layer instanceof L.Circle ||
        layer instanceof L.CircleMarker ||
        layer instanceof L.Point
      ) {
        const { lat, lng } = layer.getLatLng();
        newGeo.features.push({
          type: "Feature",
          properties: {
            radius: layer.getRadius(),
          },
          geometry: {
            type: "Point",
            coordinates: [lng, lat],
          },
        });
        return true;
      }

      if (
        layer instanceof L.Marker ||
        layer instanceof L.Polygon ||
        layer instanceof L.Rectangle ||
        layer instanceof L.Polyline
      ) {
        newGeo.features.push(layer.toGeoJSON());
        return true;
      }
    });

    return newGeo;
  };

  const calculateTotalAreaAndPerimeter = (): MapMeasurementTotals => {
    const area = {
      feet: 0,
      yards: 0,
    };
    const perimeter = {
      feet: 0,
      yards: 0,
    };

    featureGroup.current?.eachLayer((layer) => {
      if (layer._removing) {
        return true;
      }

      if (layer instanceof L.Polygon) {
        // Calculate area
        area.feet += Math.ceil(turf.area(layer.toGeoJSON()) * meterConversions.squareFeet);
        area.yards += Math.ceil(turf.area(layer.toGeoJSON()) * meterConversions.squareYards);

        // Calculate perimeter by summing individual line lengths
        const latLngs = layer.getLatLngs()[0];
        let layerPerimeterFeet = 0;
        let layerPerimeterYards = 0;

        for (let i = 0; i < latLngs.length; i++) {
          const coord = latLngs[i];
          const nextCoord = i + 1 === latLngs.length ? latLngs[0] : latLngs[i + 1];
          const segmentCoords = getValidSegmentCoords(
            coord,
            nextCoord,
            "calculateTotalAreaAndPerimeter:polygon"
          );
          if (!segmentCoords) {
            continue;
          }

          const [startCoords, endCoords] = segmentCoords;
          const startPoint = turf.point(startCoords);
          const endPoint = turf.point(endCoords);

          layerPerimeterFeet += Math.round(turf.distance(startPoint, endPoint, { units: "feet" }));
          layerPerimeterYards += Math.round(
            turf.distance(startPoint, endPoint, { units: "yards" })
          );
        }

        perimeter.feet += layerPerimeterFeet;
        perimeter.yards += layerPerimeterYards;

        console.log(
          `Polygon perimeter calculated: ${layerPerimeterFeet}ft / ${layerPerimeterYards}yd`
        );

        return true;
      }

      if (layer instanceof L.Polyline) {
        // Calculate perimeter by summing individual line lengths
        const latLngs = layer.getLatLngs();
        let layerPerimeterFeet = 0;
        let layerPerimeterYards = 0;

        for (let i = 0; i < latLngs.length - 1; i++) {
          const coord = latLngs[i];
          const nextCoord = latLngs[i + 1];
          const segmentCoords = getValidSegmentCoords(
            coord,
            nextCoord,
            "calculateTotalAreaAndPerimeter:polyline"
          );
          if (!segmentCoords) {
            continue;
          }

          const [startCoords, endCoords] = segmentCoords;
          const startPoint = turf.point(startCoords);
          const endPoint = turf.point(endCoords);

          layerPerimeterFeet += Math.round(turf.distance(startPoint, endPoint, { units: "feet" }));
          layerPerimeterYards += Math.round(
            turf.distance(startPoint, endPoint, { units: "yards" })
          );
        }

        perimeter.feet += layerPerimeterFeet;
        perimeter.yards += layerPerimeterYards;

        console.log(
          `Polyline perimeter calculated: ${layerPerimeterFeet}ft / ${layerPerimeterYards}yd`
        );

        return true;
      }

      // Handle Rectangle perimeter calculation
      if (layer instanceof L.Rectangle) {
        // Calculate area
        area.feet += Math.ceil(turf.area(layer.toGeoJSON()) * meterConversions.squareFeet);
        area.yards += Math.ceil(turf.area(layer.toGeoJSON()) * meterConversions.squareYards);

        // Calculate perimeter by summing individual line lengths
        const latLngs = layer.getLatLngs()[0];
        let layerPerimeterFeet = 0;
        let layerPerimeterYards = 0;

        for (let i = 0; i < latLngs.length; i++) {
          const coord = latLngs[i];
          const nextCoord = i + 1 === latLngs.length ? latLngs[0] : latLngs[i + 1];
          const segmentCoords = getValidSegmentCoords(
            coord,
            nextCoord,
            "calculateTotalAreaAndPerimeter:rectangle"
          );
          if (!segmentCoords) {
            continue;
          }

          const [startCoords, endCoords] = segmentCoords;
          const startPoint = turf.point(startCoords);
          const endPoint = turf.point(endCoords);

          layerPerimeterFeet += Math.round(turf.distance(startPoint, endPoint, { units: "feet" }));
          layerPerimeterYards += Math.round(
            turf.distance(startPoint, endPoint, { units: "yards" })
          );
        }

        perimeter.feet += layerPerimeterFeet;
        perimeter.yards += layerPerimeterYards;

        // console.log(`Rectangle perimeter calculated: ${layerPerimeterFeet}ft / ${layerPerimeterYards}yd`);

        return true;
      }

      // Handle Circle perimeter calculation
      if (layer instanceof L.Circle) {
        // Calculate area
        area.feet += Math.ceil(turf.area(layer.toGeoJSON()) * meterConversions.squareFeet);
        area.yards += Math.ceil(turf.area(layer.toGeoJSON()) * meterConversions.squareYards);

        // For circle, calculate perimeter using 2 * π * radius
        const radius = layer.getRadius(); // Radius in meters
        const radiusFeet = radius * 3.28084; // Convert meters to feet
        const radiusYards = radius * 1.09361; // Convert meters to yards

        const circlePerimeterFeet = Math.round(2 * Math.PI * radiusFeet);
        const circlePerimeterYards = Math.round(2 * Math.PI * radiusYards);

        perimeter.feet += circlePerimeterFeet;
        perimeter.yards += circlePerimeterYards;

        // console.log(`Circle perimeter calculated: ${circlePerimeterFeet}ft / ${circlePerimeterYards}yd`);

        return true;
      }
    });

    // console.log(`Total perimeter calculated: ${perimeter.feet}ft / ${perimeter.yards}yd`);

    return {
      area,
      perimeter,
    };
  };

  const buildMapOutput = (addressOverride?: string): MapOutput => {
    const geojson = generateGeojson();
    const geojsonWithTextBoxes = {
      ...geojson,
      textBoxes,
      properties: {
        ...(geojson as any).properties,
        textBoxes,
      },
    };
    return {
      geojson: geojsonWithTextBoxes,
      measurements: mapTotals,
      address: selectedAddress,
      addressOverride,
      textBoxes,
    };
  };

  const hadFeatureLayersRef = React.useRef(false);

  const emitMapChange = (): void => {
    if (!onChangeRef.current) {
      return;
    }

    const layerCount = featureGroup.current?.getLayers()?.length ?? 0;
    if (layerCount === 0 && hadFeatureLayersRef.current) {
      return;
    }
    if (layerCount > 0) {
      hadFeatureLayersRef.current = true;
    }

    onChangeRef.current(buildMapOutput());
  };

  const onSaveClick = async (addressOverride?: string): Promise<boolean | void> => {
    if (!onSave) {
      return;
    }

    if (guidedControls && !selectedAddress?.line1) {
      alert("Invalid address, missing Address Line 1");
      return;
    }

    return onSave(buildMapOutput(addressOverride));
  };

  const toggleDrag = () => {
    if (isDragging) {
      editingLayer.current.layer._dragging = false;
      editingLayer.current?.layer?.dragging?.disable();

      setIsDragging(false);
      return;
    }

    editingLayer.current.layer._previousLatlng = editingLayer.current.layer._latlng;
    editingLayer.current.layer._dragging = true;
    editingLayer.current?.layer?.dragging?.enable();
    setIsDragging(true);
  };

  const deleteEditingLayer = () => {
    if (!editingLayer.current.layer) {
      return;
    }

    if (editingLayerType === "text") {
      const marker = editingLayer.current.layer as L.Marker;
      const textBoxId = marker?._textBoxId;
      if (textBoxId) {
        setTextBoxes((prev) => prev.filter((box) => box.id !== textBoxId));
        textBoxMarkers.current.delete(textBoxId);
      }
      marker.remove();
      editingLayer.current.layer = null;
      setEditingLayerType(null);
      setIsEditing(false);
      setSelectedTextBoxId(null);
      return;
    }

    editingLayer.current.layer.removeFrom(map.pm._getContainingLayer());
    editingLayer.current.layer?.remove();
    editingLayer.current.layer = null;
    setEditingLayerType(null);
  };

  const clearMap = (): void => {
    deleteEditingLayer();

    const layers = featureGroup.current.getLayers();
    for (const layer of layers) {
      layer.removeFrom(map.pm._getContainingLayer());
      layer.remove();
    }

    textBoxMarkers.current.forEach((marker) => marker.remove());
    textBoxMarkers.current.clear();
    setTextBoxes([]);
    setSelectedTextBoxId(null);
    setTextToolActive(false);
    emitMapChange();
  };

  const undoLastDrawPoint = (): void => {
    try {
      // Handle drawing mode
      if (mapMode.current === "drawing" && currentDrawShape) {
        // Only enable undo for polygon, line drawing modes
        if (!["Polygon", "Line"].includes(currentDrawShape)) {
          return;
        }

        let drawInstance: any;
        let shapeName = currentDrawShape;

        if (currentDrawShape === "Polygon" && map.pm.Draw.Polygon && map.pm.Draw.Polygon._layer) {
          drawInstance = map.pm.Draw.Polygon;
        } else if (currentDrawShape === "Line" && map.pm.Draw.Line && map.pm.Draw.Line._layer) {
          drawInstance = map.pm.Draw.Line;
        }

        if (drawInstance) {
          const stateValidation = validateDrawingState(drawInstance);
          if (!stateValidation.valid) {
            console.warn("Drawing state validation failed:", stateValidation.issues);
          }

          const success = removeLastVertex(map, drawInstance, shapeName);
          if (!success) {
            console.log("Falling back to drawing mode restart");
            restartDrawingMode(map, shapeName);
          }
        }
      }
    } catch (error) {
      console.error("Error during undo operation:", error);

      try {
        if (currentDrawShape) {
          restartDrawingMode(map, currentDrawShape);
        }
      } catch (fallbackError) {
        console.error("Fallback failed:", fallbackError);
      }
    }
  };

  const canUndo = (): boolean => {
    // Allow undo only when drawing polygon or line
    if (!map) return false;

    // Check if map is actually in drawing mode for polygon or line
    const isPolygonDrawing = map?.pm?.Draw?.Polygon?._enabled;
    const isLineDrawing = map?.pm?.Draw?.Line?._enabled;

    // Allow undo if:
    // 1. Currently in drawing mode with polygon/line, OR
    // 2. Polygon/Line drawing tool is enabled (even if not actively drawing)
    const canUndoResult =
      (mapMode.current === "drawing" && ["Polygon", "Line"].includes(currentDrawShape)) ||
      isPolygonDrawing ||
      isLineDrawing;

    return canUndoResult;
  };

  const toggleDragLabel = (active: boolean | null) => {
    const draggingEnabled: boolean = active ?? isDragging;
    for (const layer of featureGroup.current?.getLayers()) {
      if (layer?.label) {
        // if isDragging = true, user is clicking the 'stop drag label' button
        if (draggingEnabled) {
          layer.label.dragging.disable();
        } else {
          layer.label.dragging.enable();
        }
      }
    }

    setIsDragging(!draggingEnabled);
  };

  const selectPlace = (place: any, coords?: MapCoords) => {
    if (place === null) {
      setSelectedAddress(null);
      onAddressChangeRef.current?.(null);
      return;
    }

    const location = place.geometry.location;
    const latLon = new L.LatLng(location.lat(), location.lng());
    const bounds = latLon.toBounds(100); // 500 = metres
    map.panTo(latLon).fitBounds(bounds);
    const addressParts = getAddressObject(place.address_components);
    if (
      guidedControls &&
      ((!addressParts.home && !addressParts.street) || !location.lat() || !location.lng())
    ) {
      setSelectedAddress(null);
      onAddressChangeRef.current?.(null);
      alert("Invalid Address, Line 1 is required");
      return;
    }
    const nextAddress: MapAddress = {
      line1: `${addressParts.home} ${addressParts.street}`.trim(),
      line2: getAddressLine2FromPlace(place),
      city: addressParts?.city,
      state: addressParts?.region,
      zip: addressParts?.postal_code,
      country: addressParts?.country,
      lat: location.lat(),
      lng: location.lng(),
    };
    setSelectedAddress(nextAddress);
    onAddressChangeRef.current?.(nextAddress);
  };

  const addLayerLabel = (layer: L.Layer, isTouch: boolean): void => {
    // Remove existing label first to prevent duplicates
    if (layer.label) {
      removeLayerLabels(map, layer);
    }

    const content = getLayerTooltipContent(layer);
    if (!content) {
      return;
    }

    const position = getLayerLabelPosition(layer);
    if (!position) {
      return;
    }

    const layerTooltipState = layer.feature?.properties?.showTooltip ?? false;

    const marker = L.marker(position, {
      icon: new LayerInformationIcon({
        content,
        color: layer.feature?.properties?.color,
        showTooltip: layerTooltipState,
        scale: labelScale,
        onCancel: () => {
          layer.feature.properties.labelLatLng = false;

          removeLayerLabels(map, layer);
          removeLayerLabelLine(map, layer);
          if (isTouch) {
            toggleDragLabel(false);
            setIsDragging(false);

            editingLayer.current.layer?.pm?.enable();
            editingLayer.current.layer = null;
            editingLayer.current.parent = null;
            mapMode.current = "null";
            setEditingLayerType(null);
            setIsEditing(false);
            safeIconUnhighlight(editingLayer.current.layer);
          }
        },
      }),
      pane: "tooltipPane",
      pmIgnore: false,
      snapIgnore: true,
    }).addTo(map);

    layer.label = marker;

    // if it's not a touch device, enable drag for layer labels
    if (!isTouch) {
      marker?.pm.enableLayerDrag();
    }

    marker?.on("click", (e) => {
      L.DomEvent.stopPropagation(e);

      if (isTouch) {
        if (editingLayer.current.layer) {
          if (editingLayer.current.layer instanceof L.Marker) {
            safeIconUnhighlight(editingLayer.current.layer);
          }
          toggleDragLabel(true);
          setIsDragging(false);
        }

        editingLayer.current.layer?.pm?.disable();
        editingLayer.current.layer = marker;
        editingLayer.current.parent = layer;
        mapMode.current = "editing";
        setEditingLayerType("label");
        setIsEditing(true);
        safeIconHighlight(editingLayer.current.layer);
      }
    });

    layer.label?.on("dblclick", (e) => {
      L.DomEvent.stopPropagation(e);

      layer.pm.enable();
    });

    let dragEventName = "pm:drag";
    let dragEndEventName = "pm:dragend";

    // this event handler is only for touch devices
    if (isTouch) {
      dragEndEventName = "dragend";
    }

    layer.label?.on(dragEndEventName, (e) => {
      layer.feature.properties.labelLatLng = e.target._latlng;
    });

    if (!(layer instanceof L.Marker)) {
      layer.label?.on("mouseover", () => {
        layer?.setStyle({
          weight: 5,
        });
      });

      layer.label?.on("mouseout", () => {
        layer?.setStyle({
          weight: 3,
        });
      });
    } else if (layer instanceof L.Marker) {
      addLayerLabelLine(map, layer, position);

      // this event handler is only for touch devices
      if (isTouch) {
        dragEventName = "drag";
      }

      layer.label?.on(dragEventName, (e) => {
        repositionLayerLabelLine(layer, getCenterOfLayer(layer), [
          e.target._latlng.lat,
          e.target._latlng.lng,
        ]);
      });
    }
  };

  useEffect(() => {
    if (!map) {
      return;
    }
    const colorTarget =
      (highlightedLayerRef.current as L.Layer | null) ?? editingLayer.current.layer;

    if (colorTarget) {
      if (colorTarget instanceof L.Marker) {
        if (colorTarget.feature) {
          colorTarget.feature.properties.color = layerColor;
          safeIconChangeColor(colorTarget, layerColor);
          emitMapChange();
        }
        return;
      }

      colorTarget?.setStyle({
        color: layerColor,
        fillColor: layerColor,
        fillOpacity: 0.3,
      });
      if (colorTarget.feature) {
        colorTarget.feature.properties.color = layerColor;
      }
      emitMapChange();

      return;
    }

    if (currentDrawShape === "Marker") {
      map.pm.Draw.Marker.options.markerStyle.icon.changeColor(
        layerColor,
        map.pm.Draw.Marker._layer
      );
    }

    defaultLayerColor.current = layerColor;
    map.pm.setGlobalOptions({
      markerEditable: true,

      templineStyle: {
        ...map.pm.getGlobalOptions().templineStyle,
        color: layerColor,
        fillColor: layerColor,
      },
      hintlineStyle: {
        ...map.pm.getGlobalOptions().hintlineStyle,
        color: layerColor,
        fillColor: layerColor,
      },
    });

    map.pm.setPathOptions(
      {
        color: layerColor,
        weight: 3,
        fillColor: layerColor,
        fillOpacity: 0.3,
      },
      {
        merge: true,
      }
    );
    if (
      mapMode.current === "drawing" &&
      currentDrawShape !== "FreePolygon" &&
      currentDrawShape !== "FreePolyline"
    ) {
      map.pm.disableDraw();
      map.pm.enableDraw(currentDrawShape, {
        snappable: false,
      });
    }
  }, [layerColor, map, currentDrawShape]);

  // Screenshot functionality - Map-to-Canvas conversion approach

  // Helper function to convert lat/lng to tile coordinates
  const deg2tile = (lat: number, lon: number, zoom: number) => {
    const latRad = (lat * Math.PI) / 180;
    const n = Math.pow(2, zoom);
    const x = Math.floor(((lon + 180) / 360) * n);
    const y = Math.floor(((1 - Math.asinh(Math.tan(latRad)) / Math.PI) / 2) * n);
    return { x, y };
  };

  // Helper function to convert tile coordinates to lat/lng
  const tile2deg = (x: number, y: number, zoom: number) => {
    const n = Math.pow(2, zoom);
    const lonDeg = (x / n) * 360 - 180;
    const latRad = Math.atan(Math.sinh(Math.PI * (1 - (2 * y) / n)));
    const latDeg = (latRad * 180) / Math.PI;
    return { lat: latDeg, lng: lonDeg };
  };

  const extractExistingTiles = async (): Promise<{
    images: { img: HTMLImageElement; bounds: L.LatLngBounds }[];
    success: boolean;
  }> => {
    try {
      const mapContainer = document.getElementById("map-container");
      if (!mapContainer) {
        return { images: [], success: false };
      }

      const tileImages = mapContainer.querySelectorAll(
        'img[src*="maps.googleapis.com"], img[src*="maps.gstatic.com"], img[src*="google"]'
      );

      const extractedTiles: { img: HTMLImageElement; bounds: L.LatLngBounds }[] = [];

      for (let i = 0; i < tileImages.length; i++) {
        const tileImg = tileImages[i] as HTMLImageElement;

        if (tileImg.complete && tileImg.naturalWidth > 0) {
          const newImg = new Image();
          newImg.width = tileImg.naturalWidth;
          newImg.height = tileImg.naturalHeight;

          try {
            const tempCanvas = document.createElement("canvas");
            tempCanvas.width = tileImg.naturalWidth;
            tempCanvas.height = tileImg.naturalHeight;
            const tempCtx = tempCanvas.getContext("2d");

            if (tempCtx) {
              tempCtx.drawImage(tileImg, 0, 0);
              const dataUrl = tempCanvas.toDataURL("image/png");
              newImg.src = dataUrl;

              const currentBounds = map.getBounds();
              extractedTiles.push({
                img: newImg,
                bounds: currentBounds,
              });
            }
          } catch (error) {
            // Silently continue on error
          }
        }
      }

      return { images: extractedTiles, success: extractedTiles.length > 0 };
    } catch (error) {
      return { images: [], success: false };
    }
  };

  const loadCurrentMapTiles = async (
    bounds: L.LatLngBounds,
    zoom: number
  ): Promise<{ tileInfo: any[]; tileBounds: L.LatLngBounds }> => {
    const northWest = deg2tile(bounds.getNorth(), bounds.getWest(), zoom);
    const southEast = deg2tile(bounds.getSouth(), bounds.getEast(), zoom);

    const minTileX = Math.min(northWest.x, southEast.x);
    const maxTileX = Math.max(northWest.x, southEast.x);
    const minTileY = Math.min(northWest.y, southEast.y);
    const maxTileY = Math.max(northWest.y, southEast.y);

    const tileBoundsNW = tile2deg(minTileX, minTileY, zoom);
    const tileBoundsSE = tile2deg(maxTileX + 1, maxTileY + 1, zoom);
    const tileBounds = L.latLngBounds(
      L.latLng(tileBoundsSE.lat, tileBoundsNW.lng),
      L.latLng(tileBoundsNW.lat, tileBoundsSE.lng)
    );

    const tileSources = [
      `https://mt1.google.com/vt/lyrs=y&x={x}&y={y}&z={z}`,
      `https://a.tile.openstreetmap.org/{z}/{x}/{y}.png`,
    ];

    const tilePromises: Promise<{ img: HTMLImageElement; x: number; y: number }>[] = [];

    for (let x = minTileX; x <= maxTileX; x++) {
      for (let y = minTileY; y <= maxTileY; y++) {
        const tilePromise = new Promise<{ img: HTMLImageElement; x: number; y: number }>(
          (resolve) => {
            let currentSourceIndex = 0;

            const tryNextSource = () => {
              if (currentSourceIndex >= tileSources.length) {
                const placeholderImg = new Image();
                placeholderImg.width = 256;
                placeholderImg.height = 256;
                resolve({ img: placeholderImg, x, y });
                return;
              }

              const tileUrl = tileSources[currentSourceIndex]
                .replace("{x}", x.toString())
                .replace("{y}", y.toString())
                .replace("{z}", zoom.toString());

              const img = new Image();
              img.crossOrigin = "anonymous";

              img.onload = () => {
                resolve({ img, x, y });
              };

              img.onerror = () => {
                currentSourceIndex++;
                tryNextSource();
              };

              img.src = tileUrl;
            };

            tryNextSource();
          }
        );

        tilePromises.push(tilePromise);
      }
    }

    try {
      const loadedTiles = await Promise.all(tilePromises);
      return { tileInfo: loadedTiles, tileBounds };
    } catch (error) {
      return { tileInfo: [], tileBounds };
    }
  };

  // Helper function to convert LatLng to pixel coordinates on canvas
  const latLngToCanvasPoint = (
    latlng: L.LatLng,
    bounds: L.LatLngBounds,
    canvasWidth: number,
    canvasHeight: number
  ) => {
    const x =
      ((latlng.lng - bounds.getWest()) / (bounds.getEast() - bounds.getWest())) * canvasWidth;
    const y =
      ((bounds.getNorth() - latlng.lat) / (bounds.getNorth() - bounds.getSouth())) * canvasHeight;
    return { x, y };
  };

  // Helper function to draw polygon on canvas
  const drawPolygonOnCanvas = (
    ctx: CanvasRenderingContext2D,
    polygon: L.Polygon,
    bounds: L.LatLngBounds,
    canvasWidth: number,
    canvasHeight: number
  ) => {
    const latlngs = polygon.getLatLngs()[0] as L.LatLng[];
    if (!latlngs || latlngs.length === 0) return;

    const color = polygon.options.color || "#f44336";
    const fillColor = polygon.options.fillColor || color;
    const fillOpacity = polygon.options.fillOpacity || 0.3;

    ctx.beginPath();
    latlngs.forEach((latlng, index) => {
      const point = latLngToCanvasPoint(latlng, bounds, canvasWidth, canvasHeight);
      if (index === 0) {
        ctx.moveTo(point.x, point.y);
      } else {
        ctx.lineTo(point.x, point.y);
      }
    });
    ctx.closePath();

    // Fill polygon
    ctx.fillStyle = fillColor;
    ctx.globalAlpha = fillOpacity;
    ctx.fill();

    // Stroke polygon
    ctx.globalAlpha = 1;
    ctx.strokeStyle = color;
    ctx.lineWidth = polygon.options.weight || 3;
    ctx.stroke();
  };

  const drawPolylineOnCanvas = (
    ctx: CanvasRenderingContext2D,
    polyline: L.Polyline,
    bounds: L.LatLngBounds,
    canvasWidth: number,
    canvasHeight: number
  ) => {
    const latlngs = polyline.getLatLngs() as L.LatLng[];
    if (!latlngs || latlngs.length === 0) return;

    const color = polyline.options.color || "#f44336";

    ctx.beginPath();
    latlngs.forEach((latlng, index) => {
      const point = latLngToCanvasPoint(latlng, bounds, canvasWidth, canvasHeight);
      if (index === 0) {
        ctx.moveTo(point.x, point.y);
      } else {
        ctx.lineTo(point.x, point.y);
      }
    });

    ctx.strokeStyle = color;
    ctx.lineWidth = polyline.options.weight || 3;
    ctx.stroke();
  };

  // Helper function to draw marker on canvas
  const drawMarkerOnCanvas = (
    ctx: CanvasRenderingContext2D,
    marker: L.Marker,
    bounds: L.LatLngBounds,
    canvasWidth: number,
    canvasHeight: number
  ) => {
    const latlng = marker.getLatLng();
    const point = latLngToCanvasPoint(latlng, bounds, canvasWidth, canvasHeight);

    // Draw a simple circle for marker
    ctx.beginPath();
    ctx.arc(point.x, point.y, 8, 0, 2 * Math.PI);
    ctx.fillStyle = "#ff0000";
    ctx.fill();
    ctx.strokeStyle = "#ffffff";
    ctx.lineWidth = 2;
    ctx.stroke();
  };

  const extractLabelLines = (html: string) => {
    if (!html) return [];

    const withLineBreaks = html.replace(/<br\s*\/?>/gi, "\n");
    const withSuperscripts = withLineBreaks.replace(/<sup>(.*?)<\/sup>/gi, "^$1");
    const withoutTags = withSuperscripts.replace(/<\/?strong>/gi, "").replace(/<[^>]+>/g, "");

    return withoutTags
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean);
  };

  const parsePixelValue = (value: string | null | undefined, fallback: number) => {
    if (!value) {
      return fallback;
    }

    const parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  };

  const getComputedLineHeight = (
    computedStyle: CSSStyleDeclaration,
    fontSize: number,
    fallbackMultiplier = layerLabelStyle.lineHeightMultiplier
  ) => {
    const rawLineHeight = computedStyle.lineHeight;
    if (!rawLineHeight || rawLineHeight === "normal") {
      return Math.ceil(fontSize * fallbackMultiplier);
    }

    return parsePixelValue(rawLineHeight, Math.ceil(fontSize * fallbackMultiplier));
  };

  const getLabelCanvasLayout = (
    marker: L.Marker,
    canvasWidth: number,
    canvasHeight: number
  ): {
    x: number;
    y: number;
    boxWidth: number;
    boxHeight: number;
    fontSize: number;
    lineHeight: number;
    paddingX: number;
    paddingY: number;
    borderWidth: number;
    textColor: string;
    backgroundColor: string;
  } | null => {
    const mapContainer = map?.getContainer?.();
    const labelElement = marker.getElement()?.querySelector(".content") as HTMLDivElement | null;

    if (!mapContainer || !labelElement) {
      return null;
    }

    const containerRect = mapContainer.getBoundingClientRect();
    const labelRect = labelElement.getBoundingClientRect();

    if (!containerRect.width || !containerRect.height || !labelRect.width || !labelRect.height) {
      return null;
    }

    const scaleX = canvasWidth / containerRect.width;
    const scaleY = canvasHeight / containerRect.height;
    const scale = (scaleX + scaleY) / 2;
    const computedStyle = window.getComputedStyle(labelElement);

    const rawFontSize = parsePixelValue(computedStyle.fontSize, layerLabelStyle.fontSize);
    const rawLineHeight = getComputedLineHeight(computedStyle, rawFontSize);
    const rawPaddingLeft = parsePixelValue(computedStyle.paddingLeft, layerLabelStyle.paddingX);
    const rawPaddingTop = parsePixelValue(computedStyle.paddingTop, layerLabelStyle.paddingY);
    const rawBorderWidth = parsePixelValue(
      computedStyle.borderTopWidth,
      layerLabelStyle.borderWidth
    );

    let x = (labelRect.left - containerRect.left) * scaleX;
    let y = (labelRect.top - containerRect.top) * scaleY;
    let boxWidth = labelRect.width * scaleX;
    let boxHeight = labelRect.height * scaleY;
    let fontSize = rawFontSize * scale;
    let lineHeight = rawLineHeight * scale;
    let paddingX = rawPaddingLeft * scaleX;
    let paddingY = rawPaddingTop * scaleY;
    let borderWidth = Math.max(1, rawBorderWidth * scale);

    const minFontScale = layerLabelExportMinimums.fontSize / fontSize;
    const minWidthScale = layerLabelExportMinimums.minWidth / boxWidth;
    const minHeightScale = layerLabelExportMinimums.minHeight / boxHeight;
    const readabilityScale = Math.max(1, minFontScale, minWidthScale, minHeightScale);

    if (readabilityScale > 1) {
      const centerX = x + boxWidth / 2;
      const centerY = y + boxHeight / 2;

      boxWidth *= readabilityScale;
      boxHeight *= readabilityScale;
      fontSize *= readabilityScale;
      lineHeight *= readabilityScale;
      paddingX *= readabilityScale;
      paddingY *= readabilityScale;
      borderWidth = Math.max(layerLabelExportMinimums.borderWidth, borderWidth * readabilityScale);

      x = centerX - boxWidth / 2;
      y = centerY - boxHeight / 2;
    }

    if (
      boxWidth < layerLabelExportMinimums.minWidth ||
      boxHeight < layerLabelExportMinimums.minHeight
    ) {
      const centerX = x + boxWidth / 2;
      const centerY = y + boxHeight / 2;

      boxWidth = Math.max(layerLabelExportMinimums.minWidth, boxWidth);
      boxHeight = Math.max(layerLabelExportMinimums.minHeight, boxHeight);

      x = centerX - boxWidth / 2;
      y = centerY - boxHeight / 2;
    }

    return {
      x,
      y,
      boxWidth,
      boxHeight,
      fontSize,
      lineHeight,
      paddingX: Math.max(layerLabelExportMinimums.paddingX, paddingX),
      paddingY: Math.max(layerLabelExportMinimums.paddingY, paddingY),
      borderWidth,
      textColor: computedStyle.color || layerLabelStyle.textColor,
      backgroundColor:
        computedStyle.backgroundColor || `rgba(255, 255, 255, ${layerLabelStyle.backgroundAlpha})`,
    };
  };

  const drawLabelOnCanvas = (
    ctx: CanvasRenderingContext2D,
    marker: L.Marker,
    bounds: L.LatLngBounds,
    canvasWidth: number,
    canvasHeight: number
  ) => {
    const iconOptions = marker.options?.icon?.options || {};
    const content = iconOptions.content || "";
    const showTooltip = iconOptions.showTooltip !== false;
    if (!showTooltip || !content) {
      return;
    }

    const lines = extractLabelLines(content);
    if (lines.length === 0) {
      return;
    }

    const labelLayout = getLabelCanvasLayout(marker, canvasWidth, canvasHeight);
    let x: number;
    let y: number;
    let boxWidth: number;
    let boxHeight: number;
    let fontSize: number;
    let lineHeight: number;
    let paddingX: number;
    let paddingY: number;
    let borderWidth: number;
    let backgroundColor: string;
    let textColor: string;

    if (labelLayout) {
      ({
        x,
        y,
        boxWidth,
        boxHeight,
        fontSize,
        lineHeight,
        paddingX,
        paddingY,
        borderWidth,
        backgroundColor,
        textColor,
      } = labelLayout);
    } else {
      const latlng = marker.getLatLng();
      const point = latLngToCanvasPoint(latlng, bounds, canvasWidth, canvasHeight);

      fontSize = layerLabelExportMinimums.fontSize;
      lineHeight = Math.ceil(fontSize * layerLabelExportMinimums.lineHeightMultiplier);
      paddingX = layerLabelExportMinimums.paddingX;
      paddingY = layerLabelExportMinimums.paddingY;
      borderWidth = layerLabelExportMinimums.borderWidth;
      backgroundColor = `rgba(255, 255, 255, ${layerLabelStyle.backgroundAlpha})`;
      textColor = layerLabelStyle.textColor;

      ctx.font = `${fontSize}px ${layerLabelStyle.fontFamily}`;
      let maxLineWidth = 0;
      lines.forEach((line) => {
        const width = ctx.measureText(line).width;
        if (width > maxLineWidth) {
          maxLineWidth = width;
        }
      });

      boxWidth = maxLineWidth + paddingX * 2;
      boxHeight = lines.length * lineHeight + paddingY * 2;
      x = point.x - boxWidth / 2;
      y = point.y - boxHeight / 2;
    }

    ctx.font = `${fontSize}px ${layerLabelStyle.fontFamily}`;
    ctx.textBaseline = "top";

    if (x + boxWidth > canvasWidth) {
      x = Math.max(0, canvasWidth - boxWidth - 2);
    }
    if (y + boxHeight > canvasHeight) {
      y = Math.max(0, canvasHeight - boxHeight - 2);
    }
    if (x < 0) {
      x = 0;
    }
    if (y < 0) {
      y = 0;
    }

    ctx.fillStyle = backgroundColor;
    ctx.strokeStyle = iconOptions.color || "#f44336";
    ctx.lineWidth = borderWidth;
    ctx.beginPath();
    ctx.rect(x, y, boxWidth, boxHeight);
    ctx.fill();
    ctx.stroke();

    ctx.fillStyle = textColor;
    lines.forEach((line, index) => {
      ctx.fillText(line, x + paddingX, y + paddingY + lineHeight * index);
    });
  };

  const isLabelMarker = (marker: L.Marker) => {
    const iconOptions = marker.options?.icon?.options || {};
    const className = iconOptions.className || "";

    return marker.options?.pane === "tooltipPane" || className.includes("leaflet-information-icon");
  };

  const shouldSkipMarkerInScreenshot = (marker: L.Marker) => {
    const iconOptions = marker.options?.icon?.options || {};
    const className = iconOptions.className || "";

    return (
      marker.options?.pmIgnore === true ||
      marker.options?.pane === "tooltipPane" ||
      className.includes("leaflet-information-icon") ||
      className.includes("leaflet-div-icon") ||
      className.includes("leaflet-textbox-icon")
    );
  };

  const hexToRgba = (hex: string, alpha: number) => {
    if (!hex) {
      return `rgba(255, 255, 255, ${alpha})`;
    }

    const trimmed = hex.trim();
    if (trimmed.startsWith("rgb")) {
      return trimmed;
    }

    const normalized = trimmed.replace("#", "");
    if (normalized.length !== 6) {
      return `rgba(255, 255, 255, ${alpha})`;
    }

    const r = parseInt(normalized.slice(0, 2), 16);
    const g = parseInt(normalized.slice(2, 4), 16);
    const b = parseInt(normalized.slice(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  const drawTextBoxOnCanvas = (
    ctx: CanvasRenderingContext2D,
    textBox: MapTextBox,
    bounds: L.LatLngBounds,
    canvasWidth: number,
    canvasHeight: number
  ) => {
    const point = latLngToCanvasPoint(
      L.latLng(textBox.lat, textBox.lng),
      bounds,
      canvasWidth,
      canvasHeight
    );

    const paddingX = 10;
    const paddingY = 8;
    const maxWidth = Math.max(120, textBox.width || 200);
    const fontSize = Math.max(10, textBox.fontSize || 14);

    ctx.font = `${fontSize}px Arial`;
    ctx.textBaseline = "top";

    const lines: string[] = [];
    const paragraphs = (textBox.text || "").split("\n");

    paragraphs.forEach((paragraph, index) => {
      const words = paragraph.split(" ");
      let currentLine = "";

      words.forEach((word) => {
        const testLine = currentLine ? `${currentLine} ${word}` : word;
        const testWidth = ctx.measureText(testLine).width;

        if (testWidth > maxWidth - paddingX * 2 && currentLine) {
          lines.push(currentLine);
          currentLine = word;
        } else {
          currentLine = testLine;
        }
      });

      if (currentLine) {
        lines.push(currentLine);
      }

      if (index < paragraphs.length - 1) {
        lines.push("");
      }
    });

    const lineHeight = Math.ceil(fontSize * 1.35);
    const desiredHeight =
      Math.max(60, textBox.height || lines.length * lineHeight + paddingY * 2) || 80;
    const maxTextLines = Math.max(1, Math.floor((desiredHeight - paddingY * 2) / lineHeight));
    const boxHeight = desiredHeight;

    let x = point.x - maxWidth / 2;
    let y = point.y - boxHeight / 2;

    if (x + maxWidth > canvasWidth) {
      x = canvasWidth - maxWidth - 2;
    }
    if (x < 0) {
      x = 2;
    }
    if (y + boxHeight > canvasHeight) {
      y = canvasHeight - boxHeight - 2;
    }
    if (y < 0) {
      y = 2;
    }

    ctx.fillStyle = hexToRgba(textBox.backgroundColor || "#ffffff", 0.85);
    ctx.strokeStyle = "rgba(0, 0, 0, 0.2)";
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.rect(x, y, maxWidth, boxHeight);
    ctx.fill();
    ctx.stroke();

    ctx.fillStyle = textBox.fontColor || "#111111";
    lines.slice(0, maxTextLines).forEach((line, index) => {
      ctx.fillText(line, x + paddingX, y + paddingY + index * lineHeight);
    });
  };

  const takeScreenshot = async () => {
    if (!map || !featureGroup.current) {
      return;
    }

    if (isGeneratingScreenshot) {
      return;
    }

    setIsGeneratingScreenshot(true);

    try {
      const mapContainer = document.getElementById("map-container");
      const canvasWidth = mapContainer ? mapContainer.offsetWidth : 800;
      const canvasHeight = mapContainer ? mapContainer.offsetHeight : 600;
      const bounds = map.getBounds();

      const canvas = document.createElement("canvas");
      canvas.width = canvasWidth;
      canvas.height = canvasHeight;
      const ctx = canvas.getContext("2d");

      if (!ctx) {
        throw new Error("Could not get canvas context");
      }

      ctx.fillStyle = "#f0f8ff";
      ctx.fillRect(0, 0, canvasWidth, canvasHeight);

      const currentZoom = Math.floor(map.getZoom());
      let tilesLoaded = false;
      let backgroundType = "fallback";

      const { images: existingTiles, success: extractSuccess } = await extractExistingTiles();

      if (extractSuccess && existingTiles.length > 0) {
        existingTiles.forEach(({ img, bounds: tileBounds }) => {
          try {
            const scale = Math.min(canvasWidth / img.width, canvasHeight / img.height);
            const scaledWidth = img.width * scale;
            const scaledHeight = img.height * scale;
            const x = (canvasWidth - scaledWidth) / 2;
            const y = (canvasHeight - scaledHeight) / 2;

            ctx.drawImage(img, x, y, scaledWidth, scaledHeight);
          } catch (error) {
            // Silently continue on error
          }
        });

        tilesLoaded = true;
        backgroundType = "extracted";
      } else {
        const { tileInfo: loadedTiles, tileBounds } = await loadCurrentMapTiles(
          bounds,
          currentZoom
        );

        if (loadedTiles.length > 0) {
          const successfulTiles = loadedTiles.filter((tile) => tile.img.width > 0);

          successfulTiles.forEach(({ img, x: tileX, y: tileY }) => {
            if (img.width > 0 && img.height > 0) {
              const tileLatLng1 = tile2deg(tileX, tileY, currentZoom);
              const tileLatLng2 = tile2deg(tileX + 1, tileY + 1, currentZoom);

              const topLeft = latLngToCanvasPoint(
                L.latLng(tileLatLng1.lat, tileLatLng1.lng),
                bounds,
                canvasWidth,
                canvasHeight
              );
              const bottomRight = latLngToCanvasPoint(
                L.latLng(tileLatLng2.lat, tileLatLng2.lng),
                bounds,
                canvasWidth,
                canvasHeight
              );

              const tileCanvasWidth = bottomRight.x - topLeft.x;
              const tileCanvasHeight = bottomRight.y - topLeft.y;

              try {
                ctx.drawImage(img, topLeft.x, topLeft.y, tileCanvasWidth, tileCanvasHeight);
              } catch (error) {
                // Silently continue on error
              }
            }
          });

          if (successfulTiles.length > 0) {
            tilesLoaded = true;
            backgroundType =
              loadedTiles[0] && loadedTiles[0].img.src.includes("google")
                ? "google"
                : "openstreetmap";
          }
        }
      }

      if (!tilesLoaded) {
        ctx.strokeStyle = "#e0e0e0";
        ctx.lineWidth = 1;
        ctx.setLineDash([5, 5]);

        const gridSize = 50;
        for (let x = 0; x <= canvasWidth; x += gridSize) {
          ctx.beginPath();
          ctx.moveTo(x, 0);
          ctx.lineTo(x, canvasHeight);
          ctx.stroke();
        }
        for (let y = 0; y <= canvasHeight; y += gridSize) {
          ctx.beginPath();
          ctx.moveTo(0, y);
          ctx.lineTo(canvasWidth, y);
          ctx.stroke();
        }
        ctx.setLineDash([]);
        backgroundType = "grid";
      }

      const featureGroupLayers = featureGroup.current ? featureGroup.current.getLayers() : [];
      const allMapLayers = [];
      map.eachLayer((layer) => {
        if (layer !== featureGroup.current && !(layer instanceof L.TileLayer)) {
          allMapLayers.push(layer);
        }
      });

      const initialLayers = initialAreaFeatureGroup.current
        ? initialAreaFeatureGroup.current.getLayers()
        : [];
      const allLayers = [...featureGroupLayers, ...allMapLayers, ...initialLayers];
      const uniqueLayers = [...new Set(allLayers)];

      uniqueLayers.forEach((layer) => {
        try {
          if (layer instanceof L.Polygon) {
            drawPolygonOnCanvas(ctx, layer, bounds, canvasWidth, canvasHeight);
          } else if (layer instanceof L.Polyline && !(layer instanceof L.Polygon)) {
            drawPolylineOnCanvas(ctx, layer, bounds, canvasWidth, canvasHeight);
          } else if (layer instanceof L.Marker) {
            if (isLabelMarker(layer)) {
              drawLabelOnCanvas(ctx, layer, bounds, canvasWidth, canvasHeight);
            } else if (!shouldSkipMarkerInScreenshot(layer)) {
              drawMarkerOnCanvas(ctx, layer, bounds, canvasWidth, canvasHeight);
            }
          } else if (layer instanceof L.LayerGroup || layer instanceof L.FeatureGroup) {
            layer.eachLayer((sublayer) => {
              if (sublayer instanceof L.Polygon) {
                drawPolygonOnCanvas(ctx, sublayer, bounds, canvasWidth, canvasHeight);
              } else if (sublayer instanceof L.Polyline && !(sublayer instanceof L.Polygon)) {
                drawPolylineOnCanvas(ctx, sublayer, bounds, canvasWidth, canvasHeight);
              } else if (sublayer instanceof L.Marker) {
                if (isLabelMarker(sublayer)) {
                  drawLabelOnCanvas(ctx, sublayer, bounds, canvasWidth, canvasHeight);
                } else if (!shouldSkipMarkerInScreenshot(sublayer)) {
                  drawMarkerOnCanvas(ctx, sublayer, bounds, canvasWidth, canvasHeight);
                }
              }
            });
          }
        } catch (error) {
          // Silently continue on error
        }
      });

      textBoxes.forEach((textBox) => {
        try {
          drawTextBoxOnCanvas(ctx, textBox, bounds, canvasWidth, canvasHeight);
        } catch (error) {
          // Silently continue on error
        }
      });

      const totalDrawnLayers = uniqueLayers.length;

      // Add compass rose
      const compassX = canvasWidth - 60;
      const compassY = 60;
      const compassRadius = 30;

      ctx.strokeStyle = "#333333";
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(compassX, compassY, compassRadius, 0, 2 * Math.PI);
      ctx.stroke();

      // North arrow
      ctx.fillStyle = "#ff0000";
      ctx.beginPath();
      ctx.moveTo(compassX, compassY - compassRadius + 5);
      ctx.lineTo(compassX - 8, compassY - 10);
      ctx.lineTo(compassX + 8, compassY - 10);
      ctx.closePath();
      ctx.fill();

      ctx.fillStyle = "#333333";
      ctx.font = "bold 12px Arial";
      ctx.textAlign = "center";
      ctx.fillText("N", compassX, compassY - compassRadius - 10);
      ctx.textAlign = "start";

      canvas.toBlob((blob) => {
        if (blob) {
          const url = URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.download = `map-screenshot-${new Date().getTime()}.png`;
          link.href = url;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
        } else {
          alert("Failed to create screenshot file. Please try again.");
        }

        setIsGeneratingScreenshot(false);
      }, "image/png");
    } catch (error) {
      alert("Screenshot failed: " + error.message);
      setIsGeneratingScreenshot(false);
    }
  };

  useEffect(() => {
    if (!editingLayer.current.layer || editingLayerType == "label") {
      return;
    }

    if (!layerName || layerName === "") {
      delete editingLayer.current.layer.feature.properties.labelText;
      return;
    }
    editingLayer.current.layer.feature.properties.labelText = layerName;
  }, [layerName]);

  // Save tooltip state to layer properties and update the label
  useEffect(() => {
    if (!editingLayer.current.layer || editingLayerType == "label") {
      return;
    }

    editingLayer.current.layer.feature.properties.showTooltip = showTooltip;

    // Recreate the label to reflect the new tooltip state
    if (editingLayer.current.layer.label) {
      removeLayerLabels(map, editingLayer.current.layer);
      removeLayerLabelLine(map, editingLayer.current.layer);
      addLayerLabel(editingLayer.current.layer, isTouch);
    } else {
      // If no label exists yet, create one
      addLayerLabel(editingLayer.current.layer, isTouch);
    }
  }, [showTooltip]);

  // Handle two-finger drag for mobile devices
  useEffect(() => {
    if (!map || !isTouch) {
      return;
    }

    // Disable dragging by default on touch devices
    map.dragging.disable();

    const mapContainer = map.getContainer();
    let touchCount = 0;

    const handleTouchStart = (e: TouchEvent) => {
      touchCount = e.touches.length;
      if (touchCount === 2 && !twoFingerDragEnabled) {
        setTwoFingerDragEnabled(true);
        map.dragging.enable();
      } else if (touchCount !== 2 && twoFingerDragEnabled) {
        setTwoFingerDragEnabled(false);
        map.dragging.disable();
      }
    };

    const handleTouchEnd = (e: TouchEvent) => {
      touchCount = e.touches.length;
      if (touchCount < 2 && twoFingerDragEnabled) {
        setTwoFingerDragEnabled(false);
        map.dragging.disable();
      }
    };

    const handleTouchCancel = () => {
      if (twoFingerDragEnabled) {
        setTwoFingerDragEnabled(false);
        map.dragging.disable();
      }
    };

    mapContainer.addEventListener("touchstart", handleTouchStart, { passive: true });
    mapContainer.addEventListener("touchend", handleTouchEnd, { passive: true });
    mapContainer.addEventListener("touchcancel", handleTouchCancel, { passive: true });

    return () => {
      mapContainer.removeEventListener("touchstart", handleTouchStart);
      mapContainer.removeEventListener("touchend", handleTouchEnd);
      mapContainer.removeEventListener("touchcancel", handleTouchCancel);
    };
  }, [map, isTouch, twoFingerDragEnabled]);

  // Enhanced mobile scroll prevention for free drawing modes
  useEffect(() => {
    if (!isTouch) return;

    const isFreeDrawing = currentDrawShape === "FreePolygon" || currentDrawShape === "FreePolyline";
    setFreeDrawModeActive(isFreeDrawing);

    if (isFreeDrawing) {
      // Prevent document/body scrolling during free drawing
      const originalBodyOverflow = document.body.style.overflow;
      const originalDocumentOverflow = document.documentElement.style.overflow;
      const originalBodyTouchAction = document.body.style.touchAction;

      // Disable scrolling on body and html
      document.body.style.overflow = "hidden";
      document.documentElement.style.overflow = "hidden";
      document.body.style.touchAction = "none";

      // Add event listeners to prevent any scrolling
      const preventScroll = (e: TouchEvent) => {
        // Only prevent if more than one touch (allow single finger drawing)
        if (e.touches.length > 1) {
          e.preventDefault();
        }
      };

      const preventScrollPassive = (e: Event) => {
        e.preventDefault();
      };

      // Prevent passive scroll events
      document.addEventListener("touchmove", preventScroll, { passive: false });
      document.addEventListener("scroll", preventScrollPassive, { passive: false });
      document.addEventListener("wheel", preventScrollPassive, { passive: false });

      // Cleanup function
      return () => {
        // Restore original styles
        document.body.style.overflow = originalBodyOverflow;
        document.documentElement.style.overflow = originalDocumentOverflow;
        document.body.style.touchAction = originalBodyTouchAction;

        // Remove event listeners
        document.removeEventListener("touchmove", preventScroll);
        document.removeEventListener("scroll", preventScrollPassive);
        document.removeEventListener("wheel", preventScrollPassive);
      };
    }
  }, [isTouch, currentDrawShape]);

  useEffect(() => {
    if (!map) {
      return;
    }
    if (initialArea) {
      initialAreaFeatureGroup.current = featureGroup.current;
      initialAreaFeatureGroup.current.addTo(map);

      L.geoJSON(initialArea).eachLayer((layer) => {
        if (layer instanceof L.Marker) {
          layer.setIcon(
            new ColoredDivIcon({
              color: layer.feature?.properties?.color || "#6EACDA",
            })
          );
        }

        if (layer instanceof L.Polyline) {
          layer?.setStyle({
            color: layer.feature?.properties?.color || defaultLayerColor.current,
            weight: 3,
            fillColor: layer.feature?.properties?.color || defaultLayerColor.current,
            fillOpacity: 0.3,
          });
        }

        // Set default tooltip state if not already set
        if (layer.feature?.properties && layer.feature.properties.showTooltip === undefined) {
          layer.feature.properties.showTooltip = false;
        }
        initialAreaFeatureGroup.current.addLayer(layer);
        if (layer && layer.setStyle) {
          layer?.setStyle({
            weight: 2,
            fill: true,
          });
        }
        addLayerLineLengths(map, layer, labelScale);
        addLayerLabel(layer, isTouch);
        addLayerEvents(layer);
      });

      setMapTotals(calculateTotalAreaAndPerimeter());

      if (!initialFeatures) {
        const fitted = fitSaneLayerBounds(map, initialAreaFeatureGroup.current, zoomSnap);
        if (!fitted) {
          fitToReferenceLocation(map, initialAddress, initialCenter);
        }
      }
    }
    if (initialFeatures) {
      L.geoJSON(initialFeatures, {
        onEachFeature: (feature, layer) => {
          if (layer instanceof L.Marker) {
            layer.setIcon(
              new ColoredDivIcon({
                color: feature.properties.color,
              })
            );
          }

          if (layer instanceof L.Polyline) {
            layer?.setStyle({
              color: feature.properties.color,
              weight: 3,
              fillColor: feature.properties.color,
              fillOpacity: 0.3,
            });
          }
          // Set default tooltip state if not already set
          if (layer.feature?.properties && layer.feature.properties.showTooltip === undefined) {
            layer.feature.properties.showTooltip = false;
          }
          addLayerLineLengths(map, layer, labelScale);
          addLayerLabel(layer, isTouch);
          addLayerEvents(layer);
        },
      }).eachLayer((layer) => {
        featureGroup.current.addLayer(layer);
      });

      setMapTotals(calculateTotalAreaAndPerimeter());

      const fitted = fitSaneLayerBounds(map, featureGroup.current, zoomSnap);
      if (!fitted) {
        fitToReferenceLocation(map, initialAddress, initialCenter);
      }
    }

    if (!initialFeatures && !initialArea && initialCenter) {
      const latLon = new L.LatLng(...initialCenter);
      const bounds = latLon.toBounds(100); // 500 = metres
      map.panTo(latLon).fitBounds(bounds);
    }

    map.on("pm:keyevent", (e) => {
      if (e.eventType === "keydown") {
        return;
      }

      if (e.event.code === "Backspace") {
        const activeElement = document.activeElement as HTMLElement | null;
        const isTypingTextBox =
          activeElement &&
          (activeElement.classList?.contains("map-textbox-content") ||
            activeElement.closest?.(".map-textbox"));
        if (isTypingTextBox || editingLayerType === "text") {
          return;
        }
        deleteEditingLayer();
      }

      if (e.event.code === "Escape") {
        if (editingLayer.current.layer) {
          editingLayer.current.layer.pm.disable();
          return;
        }

        if (mapMode.current === "drawing" || (map as any)._aiMappingDrawActive === true) {
          map.pm.disableDraw();
          return;
        }
      }
    });

    map.on("pm:dragend", (e) => {
      editingLayer.current.layer = e.layer;
      e.layer.pm.enable();
    });

    map.on("pm:dragstart", (e) => {
      e.layer.pm.disable();
    });
    map.on("click", (e) => {
      // if it's a touch device, this will calculate the marker's label new latlng whenever user clicks on the map, and all toggles will be false
      if (isTouch) {
        if (editingLayer.current.layer instanceof L.Marker) {
          if (
            editingLayer.current.layer?._previousLatlng &&
            editingLayer.current.layer?.feature?.properties?.labelLatLng
          ) {
            editingLayer.current.layer.feature.properties.labelLatLng =
              calculateDraggedMarkerLabelPosition(
                editingLayer.current.layer,
                editingLayer.current.layer._latlng
              );
          }
          toggleDrag();
          safeIconUnhighlight(editingLayer.current.layer);
        }

        toggleDragLabel(true);
        setIsDragging(false);
        setIsEditing(false);
      }

      // Clear any pending edit timeouts before disabling
      if (editingLayer.current.layer && editingLayer.current.layer._editTimeout) {
        clearTimeout(editingLayer.current.layer._editTimeout);
        editingLayer.current.layer._editTimeout = null;
      }

      editingLayer.current.layer?.pm?.disable();
      editingLayer.current.layer = null;
      editingLayer.current.parent = null;
      setEditingLayerType(null);
      setIsEditing(false);
      setSelectedTextBoxId(null);
      setTextToolActive(false);
    });

    map.on("pm:drawstart", ({ workingLayer, shape }) => {
      if ((map as any)._aiMappingDrawActive === true) {
        return;
      }
      setSelectedTextBoxId(null);
      setTextToolActive(false);
      if (editingLayerType === "text") {
        editingLayer.current.layer = null;
        editingLayer.current.parent = null;
        setEditingLayerType(null);
        setIsEditing(false);
      }
      setCurrentDrawShape(shape);
      mapMode.current = "drawing";
      activeDrawingLayer.current = workingLayer;

      // Add pm-draw-active class to force crosshair cursor
      map.getContainer().classList.add("pm-draw-active");
    });

    map.on("pm:drawend", () => {
      if ((map as any)._aiMappingDrawActive === true) {
        return;
      }
      activeDrawingLayer.current = null;

      // Only reset drawing state if no drawing tools are active
      const isAnyDrawingActive = map.pm.globalDrawModeEnabled();
      const isPolygonActive = map?.pm?.Draw?.Polygon?._enabled;
      const isLineActive = map?.pm?.Draw?.Line?._enabled;

      // Don't reset state if any drawing tool is still active
      if (!isAnyDrawingActive && !isPolygonActive && !isLineActive) {
        setCurrentDrawShape(null);
        mapMode.current = null;

        // Remove pm-draw-active class when drawing is completely finished
        map.getContainer().classList.remove("pm-draw-active");
      }
      // If drawing tools are still active, keep the drawing state
    });

    return () => {
      activeDrawingLayer.current = null;
      map.off("pm:dragend");
      map.off("pm:dragstart");
      map.off("click");
      map.off("pm:keyevent");
      map.off("pm:drawstart");
      map.off("pm:drawend");
    };
  }, [map]);

  useEffect(() => {
    if (!map) {
      return;
    }

    const previous = highlightedLayerRef.current as any;
    if (
      previous &&
      previous !== editingLayer.current.layer &&
      typeof previous.setStyle === "function"
    ) {
      previous.setStyle({ weight: 3 });
    }
    highlightedLayerRef.current = null;

    if (!highlightedFeatureId) {
      return;
    }

    const layers = featureGroup.current?.getLayers() ?? [];
    const match = layers.find(
      (layer: any) => layer.feature?.properties?.featureId === highlightedFeatureId
    ) as any;

    if (!match || match instanceof L.Marker || typeof match.setStyle !== "function") {
      return;
    }

    highlightedLayerRef.current = match;
    match.setStyle({ weight: 5 });
  }, [highlightedFeatureId, map]);

  useEffect(() => {
    if (!measuredAreaToolsRef) return;
    const opsContext: MeasuredAreaOpsContext = {
      map,
      featureGroup,
      isTouch,
      labelScale,
      defaultColor: () => defaultLayerColor.current ?? "#f44336",
      addLayerEvents,
      addLayerLabel,
      addLayerLineLengths,
      onEdited: () => {
        setMapTotals(calculateTotalAreaAndPerimeter());
        emitMapChange();
      },
    };
    measuredAreaToolsRef.current = {
      splitLine: (points) => splitManualAreasAlongLine(opsContext, points),
      erasePolygon: (points) => eraseManualAreasWithPolygon(opsContext, points),
      mergePolygon: (points) => mergeManualAreasWithPolygon(opsContext, points),
      remove: () => {
        const target = measuredAreaLayer();
        if (!target) return;
        featureGroup.current?.removeLayer(target);
        target.remove();
        emitMapChange();
      },
    };
    return () => {
      measuredAreaToolsRef.current = null;
    };
  }, [map, highlightedFeatureId, emitMapChange]);

  useEffect(() => {
    const source =
      (initialArea as any)?.textBoxes ||
      (initialArea as any)?.properties?.textBoxes ||
      (initialFeatures as any)?.textBoxes ||
      (initialFeatures as any)?.properties?.textBoxes;

    if (!Array.isArray(source)) {
      return;
    }

    setTextBoxes(source as MapTextBox[]);
  }, [initialArea, initialFeatures]);

  useEffect(() => {
    if (!map) {
      return;
    }

    const markers = textBoxMarkers.current;
    const nextIds = new Set(textBoxes.map((box) => box.id));

    markers.forEach((marker, id) => {
      if (!nextIds.has(id)) {
        marker.remove();
        markers.delete(id);
      }
    });

    textBoxes.forEach((textBox) => {
      const existing = markers.get(textBox.id);
      if (!existing) {
        const marker = createTextBoxMarker(textBox);
        markers.set(textBox.id, marker);
        setupTextBoxMarker(marker, textBox);
      } else {
        existing.setLatLng([textBox.lat, textBox.lng]);
        setupTextBoxMarker(existing, textBox);
      }
    });
  }, [map, textBoxes, selectedTextBoxId]);

  useEffect(() => {
    if (!selectedTextBoxId) {
      return;
    }

    const marker = textBoxMarkers.current.get(selectedTextBoxId);
    const element = marker?.getElement();
    const content = element?.querySelector(".map-textbox-content") as HTMLDivElement | null;
    content?.focus();
  }, [selectedTextBoxId]);

  useEffect(() => {
    if (!editingLayer.current.layer) {
      return;
    }
    if (map) {
      map.pm.setGlobalOptions({ snappable: false });
    }

    if (editingLayer.current.layer?.feature?.properties) {
      editingLayer.current.layer.feature.properties = {
        ...editingLayer.current.layer.feature.properties,
        showLineLengths: layerShowLineLength,
      };
    }
  }, [layerShowLineLength, map]);

  // Handle drawing hint tooltip state changes
  useEffect(() => {
    if (!map) return;

    if (map.pm.Draw.Polygon?._hintMarker) {
      syncDrawingHintTooltip(
        map.pm.Draw.Polygon._hintMarker,
        updateDrawingPolygonToolTipContent,
        currentDrawShape === "Polygon"
      );
    }

    if (map.pm.Draw.Line?._hintMarker) {
      syncDrawingHintTooltip(
        map.pm.Draw.Line._hintMarker,
        updateDrawingLineToolTipContent,
        currentDrawShape === "Line"
      );
    }
  }, [showTooltip, showLineLengthsWhileMeasuring, map, currentDrawShape, syncDrawingHintTooltip]);

  const createTextBox = () => {
    if (!map) {
      return;
    }

    if (textToolActive) {
      editingLayer.current.layer = null;
      editingLayer.current.parent = null;
      setEditingLayerType(null);
      setIsEditing(false);
      setSelectedTextBoxId(null);
      setTextToolActive(false);
      return;
    }

    if (editingLayer.current.layer) {
      editingLayer.current.layer?.pm?.disable?.();
      editingLayer.current.layer = null;
      editingLayer.current.parent = null;
      setEditingLayerType(null);
      setIsEditing(false);
    }

    const container = map.getContainer();
    const offsetX = Math.max(60, container.clientWidth - 220);
    const offsetY = 60;
    const latLng = map.containerPointToLatLng(L.point(offsetX, offsetY));

    const newTextBox: MapTextBox = {
      id: `textbox-${Date.now()}`,
      lat: latLng.lat,
      lng: latLng.lng,
      text: "Type here...",
      fontSize: 14,
      fontColor: "#111111",
      backgroundColor: "#ffffff",
      width: 200,
      height: 90,
    };

    map.pm?.disableDraw?.();
    setCurrentDrawShape(null);
    mapMode.current = null;
    setTextToolActive(true);

    setTextBoxes((prev) => [...prev, newTextBox]);
    setSelectedTextBoxId(newTextBox.id);
  };

  const updateSelectedTextBox = (updates: Partial<MapTextBox>) => {
    if (!selectedTextBoxId) {
      return;
    }

    setTextBoxes((prev) =>
      prev.map((box) => (box.id === selectedTextBoxId ? { ...box, ...updates } : box))
    );
  };

  const setupTextBoxMarker = (marker: L.Marker, textBox: MapTextBox) => {
    const element = marker.getElement();
    if (!element) {
      return;
    }

    const wrapper = element.querySelector(".map-textbox") as HTMLDivElement | null;
    const content = element.querySelector(".map-textbox-content") as HTMLDivElement | null;
    const handle = element.querySelector(".map-textbox-drag-handle") as HTMLDivElement | null;
    const resizeHandle = element.querySelector(
      ".map-textbox-resize-handle"
    ) as HTMLDivElement | null;

    if (!wrapper || !content || !handle || !resizeHandle) {
      return;
    }

    wrapper.style.width = `${Math.max(120, textBox.width)}px`;
    wrapper.style.height = `${Math.max(60, textBox.height || 90)}px`;
    wrapper.style.background = hexToRgba(textBox.backgroundColor || "#ffffff", 0.85);
    content.style.fontSize = `${Math.max(10, textBox.fontSize)}px`;
    content.style.color = textBox.fontColor;

    if (document.activeElement !== content) {
      content.innerText = textBox.text || "";
    }

    if (selectedTextBoxId === textBox.id) {
      editingLayer.current.layer = marker;
      mapMode.current = "editing";
      setEditingLayerType("text");
      setIsEditing(true);
    }

    if (readonly) {
      content.setAttribute("contenteditable", "false");
      wrapper.style.cursor = "default";
      return;
    }

    if (!marker._textBoxInitialized) {
      marker._textBoxInitialized = true;

      const handleSelect = (e: Event) => {
        L.DomEvent.stopPropagation(e);
        if (editingLayer.current.layer && editingLayer.current.layer !== marker) {
          editingLayer.current.layer?.pm?.disable?.();
        }
        editingLayer.current.layer = marker;
        mapMode.current = "editing";
        setEditingLayerType("text");
        setIsEditing(true);
        setSelectedTextBoxId(textBox.id);
        setTextToolActive(true);
      };

      const handleFocus = (e: Event) => {
        L.DomEvent.stopPropagation(e);
        if (content.innerText.trim() === "Type here...") {
          content.innerText = "";
        }
        marker.dragging?.disable();
        if (!isTouch) {
          map.dragging?.disable();
        }
      };

      const handleBlur = () => {
        marker.dragging?.enable();
        if (!isTouch) {
          map.dragging?.enable();
        }
        if (!content.innerText.trim()) {
          content.innerText = "Type here...";
        }
        updateSelectedTextBox({ text: content.innerText });
      };

      const handleInput = () => {
        setTextBoxes((prev) =>
          prev.map((box) => (box.id === textBox.id ? { ...box, text: content.innerText } : box))
        );
      };

      L.DomEvent.on(wrapper, "click", handleSelect);
      L.DomEvent.on(content, "click", handleSelect);
      L.DomEvent.on(wrapper, "mousedown", () => {
        if (document.activeElement === content) {
          content.blur();
        }
        marker.dragging?.enable();
      });
      const startManualDrag = (event: Event) => {
        event.preventDefault();
        event.stopPropagation();
        content.blur();
        if (!map) {
          return;
        }

        map.dragging?.disable();
        const container = map.getContainer();
        const rect = container.getBoundingClientRect();

        const getPointFromEvent = (evt: any) => {
          if (evt.touches && evt.touches.length) {
            return L.point(evt.touches[0].clientX - rect.left, evt.touches[0].clientY - rect.top);
          }
          return L.point(evt.clientX - rect.left, evt.clientY - rect.top);
        };

        const moveHandler = (evt: any) => {
          const point = getPointFromEvent(evt);
          const latLng = map.containerPointToLatLng(point);
          marker.setLatLng(latLng);
        };

        const endHandler = () => {
          document.removeEventListener("mousemove", moveHandler);
          document.removeEventListener("mouseup", endHandler);
          document.removeEventListener("touchmove", moveHandler);
          document.removeEventListener("touchend", endHandler);
          document.removeEventListener("touchcancel", endHandler);
          map.dragging?.enable();
          const latlng = marker.getLatLng();
          setTextBoxes((prev) =>
            prev.map((box) =>
              box.id === textBox.id ? { ...box, lat: latlng.lat, lng: latlng.lng } : box
            )
          );
        };

        document.addEventListener("mousemove", moveHandler);
        document.addEventListener("mouseup", endHandler);
        document.addEventListener("touchmove", moveHandler, { passive: false });
        document.addEventListener("touchend", endHandler);
        document.addEventListener("touchcancel", endHandler);
      };

      L.DomEvent.on(handle, "mousedown", startManualDrag);
      L.DomEvent.on(handle, "touchstart", startManualDrag);

      const startManualResize = (event: Event) => {
        event.preventDefault();
        event.stopPropagation();
        content.blur();

        const startWidth = wrapper.offsetWidth;
        const startHeight = wrapper.offsetHeight;
        const startClientX =
          (event as TouchEvent).touches?.[0]?.clientX ?? (event as MouseEvent).clientX;
        const startClientY =
          (event as TouchEvent).touches?.[0]?.clientY ?? (event as MouseEvent).clientY;

        const moveHandler = (evt: any) => {
          const clientX = evt.touches?.[0]?.clientX ?? evt.clientX;
          const clientY = evt.touches?.[0]?.clientY ?? evt.clientY;
          const nextWidth = Math.max(120, startWidth + (clientX - startClientX));
          const nextHeight = Math.max(60, startHeight + (clientY - startClientY));
          wrapper.style.width = `${nextWidth}px`;
          wrapper.style.height = `${nextHeight}px`;
        };

        const endHandler = () => {
          document.removeEventListener("mousemove", moveHandler);
          document.removeEventListener("mouseup", endHandler);
          document.removeEventListener("touchmove", moveHandler);
          document.removeEventListener("touchend", endHandler);
          document.removeEventListener("touchcancel", endHandler);
          updateSelectedTextBox({
            width: Math.max(120, wrapper.offsetWidth),
            height: Math.max(60, wrapper.offsetHeight),
          });
        };

        document.addEventListener("mousemove", moveHandler);
        document.addEventListener("mouseup", endHandler);
        document.addEventListener("touchmove", moveHandler, { passive: false });
        document.addEventListener("touchend", endHandler);
        document.addEventListener("touchcancel", endHandler);
      };

      L.DomEvent.on(resizeHandle, "mousedown", startManualResize);
      L.DomEvent.on(resizeHandle, "touchstart", startManualResize);
      L.DomEvent.on(content, "mousedown", (e: Event) => {
        L.DomEvent.stopPropagation(e);
      });
      L.DomEvent.on(content, "focus", handleFocus);
      L.DomEvent.on(content, "blur", handleBlur);
      L.DomEvent.on(content, "input", handleInput);

      L.DomEvent.disableClickPropagation(wrapper);
      L.DomEvent.disableScrollPropagation(wrapper);
    }
  };

  const createTextBoxMarker = (textBox: MapTextBox) => {
    const icon = L.divIcon({
      className: "leaflet-textbox-icon",
      html: `<div class="map-textbox" data-textbox-id="${
        textBox.id
      }"><div class="map-textbox-drag-handle" title="Drag"></div><div class="map-textbox-content" contenteditable="${
        readonly ? "false" : "true"
      }"></div><div class="map-textbox-resize-handle" title="Resize"></div></div>`,
    });

    const marker = L.marker([textBox.lat, textBox.lng], {
      icon,
      draggable: !readonly,
      pmIgnore: true,
    }) as L.Marker & { _textBoxId?: string; _textBoxInitialized?: boolean };
    marker._textBoxId = textBox.id;

    marker.on("click", (e) => {
      L.DomEvent.stopPropagation(e);
      if (editingLayer.current.layer && editingLayer.current.layer !== marker) {
        editingLayer.current.layer?.pm?.disable?.();
      }
      editingLayer.current.layer = marker;
      mapMode.current = "editing";
      setEditingLayerType("text");
      setIsEditing(true);
      setSelectedTextBoxId(textBox.id);
      setTextToolActive(true);
      const element = marker.getElement();
      const content = element?.querySelector(".map-textbox-content") as HTMLDivElement | null;
      content?.focus();
    });

    marker.on("dragend", (e) => {
      const latlng = e.target.getLatLng();
      setTextBoxes((prev) =>
        prev.map((box) =>
          box.id === textBox.id ? { ...box, lat: latlng.lat, lng: latlng.lng } : box
        )
      );
    });

    marker.on("dragstart", (e) => {
      L.DomEvent.stopPropagation(e);
    });

    marker.addTo(map);
    marker.dragging?.enable();
    return marker;
  };

  const setFeatureGroup = (fg: L.FeatureGroup) => {
    if (featureGroup.current) {
      featureGroup.current.addLayer(fg.getLayers()[0]);
      featureGroup.current.addTo(map);
    }
  };

  // Helper function to remove pm-draw-active class
  const removeDrawActiveClass = () => {
    if (map) {
      map.getContainer().classList.remove("pm-draw-active");
    }
  };

  // Custom toggleDrawMode wrapper that handles state changes
  const handleToggleDrawMode = (
    map: L.Map,
    drawShape: L.PM.SUPPORTED_SHAPES | "FreePolygon" | "FreePolyline",
    layerColor: string,
    isEditing: boolean,
    currentDrawShape: string | null
  ): void => {
    // If we're turning off the same mode that's currently active
    if (!isEditing && currentDrawShape === drawShape) {
      setCurrentDrawShape(null);
      mapMode.current = null;
      map.pm.disableDraw();
      removeDrawActiveClass();
      return;
    }

    // Handle FreePolygon mode
    if (drawShape === "FreePolygon") {
      // Disable any active geoman drawing first
      map.pm.disableDraw();
      removeDrawActiveClass();
      // Set the state to activate FreeDrawWrapper
      setCurrentDrawShape("FreePolygon");
      mapMode.current = "drawing";
      console.log("FreePolygon mode activated"); // Debug log
      return;
    }

    // Handle FreePolyline mode
    if (drawShape === "FreePolyline") {
      // Disable any active geoman drawing first
      map.pm.disableDraw();
      removeDrawActiveClass();
      // Set the state to activate FreeDrawWrapper
      setCurrentDrawShape("FreePolyline");
      mapMode.current = "drawing";
      console.log("FreePolyline mode activated"); // Debug log
      return;
    }

    // For regular geoman shapes, call the original toggleDrawMode
    toggleDrawMode(map, drawShape, layerColor, isEditing, currentDrawShape);
  };
  useEffect(() => {
    const { geojson } = buildMapOutput();
    setCurrentGeoJsonBytes(getUtf8ByteSize(JSON.stringify(geojson)));
  }, [mapTotals, textBoxes]);

  return (
    <div style={{ position: "relative" }}>
      {currentGeoJsonBytes >= MAP_WARNING_THRESHOLD_BYTES && (
        <div
          style={{
            position: "absolute",
            top: 16,
            left: 16,
            right: 16,
            zIndex: 1000,
            padding: "10px 14px",
            borderRadius: 4,
            background: "#fff3cd",
            border: "1px solid #ffca2c",
            color: "#664d03",
            fontSize: 13,
            lineHeight: 1.4,
            fontWeight: 600,
          }}
        >
          {MAP_WARNING_MESSAGE}
        </div>
      )}
      <MapContainer
        id="map-container"
        center={initialCenter || [39.8097343, -98.5556199]}
        zoom={initialCenter ? 15 : 5}
        scrollWheelZoom={true}
        zoomControl={false}
        zoomSnap={zoomSnap}
        boxZoom={false}
        dragging={!isTouch}
        style={{ minWidth: "100%", minHeight: "100%" }}
      >
        <FeatureGroup ref={featureGroup}>
          <GeomanWrapper setMap={setMap} handleChange={handleChange} />
          {map && (
            <FreeDrawWrapper
              isActive={currentDrawShape === "FreePolygon" || currentDrawShape === "FreePolyline"}
              color={layerColor}
              mode={currentDrawShape === "FreePolygon" ? "polygon" : "polyline"}
              onPolygonCreated={handleFreePolygonCreated}
              onPolylineCreated={handleFreePolylineCreated}
            />
          )}
        </FeatureGroup>
        {useOpenStreetMap ? (
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            maxZoom={19}
          />
        ) : (
          <ReactLeafletGoogleLayer useGoogMapsLoader={false} type={"hybrid"} />
        )}
      </MapContainer>
      {map && !readonly && guidedControls && (
        <GuidedControls
          map={map}
          featureGroup={setFeatureGroup}
          currentDrawShape={currentDrawShape}
          mapMode={mapMode.current}
          color={layerColor}
          setColor={setLayerColor}
          isEditing={isEditing}
          name={`${layerName}`}
          setName={setLayerName}
          showSave={typeof onSave === "function"}
          onSave={onSaveClick}
          isDragging={isDragging}
          toggleDrag={toggleDrag}
          deleteLayer={deleteEditingLayer}
          total={mapTotals}
          showLineLength={layerShowLineLength}
          setShowLineLength={setLayerShowLineLength}
          editingLayer={editingLayer.current}
          editingLayerType={editingLayerType}
          toggleDrawMode={handleToggleDrawMode}
          isTouch={isTouch}
          selectedAddress={selectedAddress}
          selectPlace={selectPlace}
          clearMap={clearMap}
          close={close}
          undoLastDrawPoint={undoLastDrawPoint}
          canUndo={canUndo()}
          saveLabel={saveLabel}
          onAddressConfirmed={onGuidedAddressConfirmed}
        />
      )}
      {map && !readonly && !guidedControls && (
        <Controls
          map={map}
          featureGroup={featureGroup.current}
          currentDrawShape={currentDrawShape}
          mapMode={mapMode.current}
          color={layerColor}
          setColor={setLayerColor}
          isEditing={isEditing}
          name={`${layerName}`}
          setName={setLayerName}
          showSave={typeof onSave === "function"}
          onSave={onSaveClick}
          isDragging={isDragging}
          toggleDrag={toggleDrag}
          deleteLayer={deleteEditingLayer}
          total={mapTotals}
          showLineLengthsWhileMeasuring={showLineLengthsWhileMeasuring}
          setShowLineLengthsWhileMeasuring={setShowLineLengthsWhileMeasuring}
          showLineLength={layerShowLineLength}
          setShowLineLength={setLayerShowLineLength}
          showTooltip={showTooltip}
          setShowTooltip={setShowTooltip}
          editingLayer={editingLayer.current}
          editingLayerType={editingLayerType}
          toggleDrawMode={handleToggleDrawMode}
          isTouch={isTouch}
          selectedAddress={selectedAddress}
          selectPlace={selectPlace}
          clearMap={clearMap}
          createTextBox={createTextBox}
          textToolActive={textToolActive}
          selectedTextBox={
            selectedTextBoxId ? textBoxes.find((box) => box.id === selectedTextBoxId) || null : null
          }
          updateSelectedTextBox={updateSelectedTextBox}
          takeScreenshot={takeScreenshot}
          isGeneratingScreenshot={isGeneratingScreenshot}
          hideLocation={hideLocation}
          goBack={goBack}
          goBackTo={goBackTo}
          goBackLabel={goBackLabel}
          showTools={showTools}
          close={close}
          undoLastDrawPoint={undoLastDrawPoint}
          canUndo={canUndo()}
          saveLabel={saveLabel}
          sidebar={controlsVariant === "sidebar"}
          sidebarContent={controlsContent}
          sidebarToolsContent={sidebarToolsContent}
          measuredAreaTools={measuredAreaTools}
          hideMeasurementTotals={hideMeasurementTotals}
          sidebarCollapsed={controlsCollapsed}
          onToggleSidebarCollapse={onToggleControlsCollapse}
          sidebarTitle={mapTitle}
        />
      )}
    </div>
  );
}
